<template>
<div class="chapter">
<div class="inner_page">
<section epub:type="chapter">
<header>
<h1 class="topictitle1" id="ch3"><a href="#/nav#nch3"><span class="section_number">Chapter 1</span></a></h1>
</header>
<p>Evelyn Yvonne McTavish—Tavish to her friends—clenched the red long-stemmed rose and stared at the glossy casket. The brief
            graveside service had ended with the Unitarian Universalist pastor reading from Kahlil Gibran’s <i>The Prophet</i>. The pastor’s last words echoed in Tavish’s mind. “And when the earth shall claim your limbs, then shall you truly dance.”
         </p>
<p>“What does that even mean?” Tavish whispered. The unseasonably warm Albuquerque sun beat down on her black wide-brimmed hat,
            giving her a pounding headache. Her dress was itchy black wool made worse by her sweat.
         </p>
<p>The few mourners drifted away to their cars lined up on the pavement behind her, each first tossing a rose from a spray onto
            the lowered coffin.
         </p>
<p>Her mother had put in an appearance at the funeral home but left before the graveside service. Mother’s stolen art had been
            released by the police and was being returned today. The stolen paintings were clearly more important than her daughter’s
            grief.
         </p>
<p>Tavish felt . . . hot. Hollow. Empty. Squeezed dry.</p>
<p>The funeral director, standing in the shade of a nearby tree, shifted and took a quick look at his watch.</p>
<p><i>Of course.</i> She was delaying them from finishing their work. Fake grass covered the pile of raw, tawny-colored earth—earth that would
            soon cover her fiancé.
         </p>
<p>She tried to picture his face. Before. The funeral home said his remains were unviewable, but they didn’t realize she’d been
            the one to find his body. That was the image burned into her brain. He’d told her where to meet him, a parking lot near the
            Rio Grande River. He was in his car—a rental, it turned out. The shotgun was beside him on the seat.
         </p>
<p>She snapped the rose’s stem in half.</p>
<p>He would have left a note. If it was suicide, he would have left her a note. If he loved her like he’d promised, he would
            have found a way to tell her why.
         </p>
<p>The late-afternoon sunlight caught the facets of her engagement ring’s diamond, sending a shimmering light pattern onto the
            coffin’s dark wood. He’d proposed just a week ago. They planned to have a celebration dinner this very night. He would have
            formally met her mother—and just as formally asked her mother for her hand in marriage.
         </p>
<p>She tore her gaze from the yawning grave and glanced at her grandmother’s headstone a short distance away. <i>Grandma, I wish you were here</i>.
         </p>
<p>She felt rather than heard someone come up behind her. She stiffened, expecting more bland condolences or maybe even a nudge
            toward her car.
         </p>
<p>“I can’t believe it’s true,” a woman’s voice murmured. “I came as fast as I could.”</p>
<p>Tavish nodded. She couldn’t believe it either. She’d walked around for the past week like a cyborg—when she could get out
            of bed. She hadn’t bothered to contact her accounting company and let them know why she was absent. They called and she’d
            just let the answering machine pick up. They’d finally stopped calling. She didn’t care to know why. What difference did that
            make? Her life was over. And she hated that company anyway. Though Tavish’s heart had been in her art ever since her grandmother
            died, her mother insisted she work “a real job” and had given her the firm. Now her mother could take it back.
         </p>
<p>“Why? Why?” the woman asked.</p>
<p><i>Why indeed? </i>Tavish wished the mourner would leave. She needed these last few moments alone with Andrew. Her love. Her loss. She’d mentally
            planned their wedding, honeymoon, first home. The baby they would make. Family Thanksgivings and Christmases together. Now
            only emptiness stretched before her.
         </p>
<p>“I had so much to say to him,” the other woman whispered. “I . . . I . . .” She quietly sobbed.</p>
<p>Tavish pulled a clean handkerchief from her pocket and held it out. The woman took it.</p>
<p>He’d brought meaning into her life, which had lost direction since Grandma’s death. Before he came along, she’d been a fat,
            ugly lump of clay. He’d thought her intelligent, beautiful, and a brilliant artist.
         </p>
<p>The weeping continued, followed by coughing and throat clearing. “This is so wrong.”</p>
<p><i>Wrong? Nothing’s been right for a long time.</i></p>
<p>“He never would have left me.” The woman’s voice steadied. “Never. Not now. We were to have been married—”</p>
<p>Her words shot through Tavish’s thoughts. “Excuse me?” She turned.</p>
<p>The strikingly beautiful young woman stared at Tavish through brown, glittering eyes. A black shift stretched across her pregnant
            belly.
         </p>
<p>Tavish tore her gaze from the woman’s midsection and took a half step backward. “Oh. It’s . . . I’m . . . This is extremely
            awkward, but I think you’re at the wrong funeral. This is . . . was Andrew James.”
         </p>
<p>“Yes. Andrew James. He’s dead, and it’s all my fault.” She stared at the lowered coffin, then shifted her gaze to Tavish.
            Her eyes narrowed. “Or maybe it’s your fault.”
         </p>
<p>Glancing around for the funeral director, Tavish hoped to catch his attention and have him escort this obviously deranged
            woman away. She spotted the man walking toward the hearse.
         </p>
<p>“I made him choose,” the woman said. She was now glaring at Tavish. “But it <i>is</i> your fault.”
         </p>
<p>Tavish wanted to put her hands over her ears. “Please go away—”</p>
<p>“I never would have done that . . . made him choose between us”—the woman shook her head—“if I’d known he’d kill himself.”</p>
<p>Tavish’s stomach churned. “He didn’t kill himself! He was murdered, and it was set up to look like suicide. I . . . found
            him.”
         </p>
<p>The woman’s gaze sharpened. “Do the police believe it was murder?”</p>
<p>“Not yet, but—”</p>
<p id="Fragments_of_Fear_4M_topic_9__p__Hlk527010584">The woman opened a faux leather tote bag, grabbed a photograph, and thrust it at Tavish. Andrew grinned out at her, his arm
            around the woman. In the background, Tavish recognized the distinctive shape of the Spirit Lodge and Spa on the edge of town.
            She sucked in air through numb lips. “That d-doesn’t prove anything.”
         </p>
<p>The woman snatched the photograph back. “You don’t have to believe me. Think about all the times Andrew was gone—”</p>
<p>“He was an investment counselor. He had to travel—”</p>
<p>“Yeah. Right. Investment counselors do most of their work on the phone.” The woman jammed the photograph back into her purse.
            “Did you ever go with him on a business trip? Did you ever meet a single client?”
         </p>
<p>“No—”</p>
<p>“And where are his clients? His friends? Family? Why didn’t anyone come to his funeral?”</p>
<p>“Um . . .”</p>
<p>“Did you ever even go to his home?”</p>
<p>“He . . . he said he had a roommate.”</p>
<p>“Yeah. Me.”</p>
<p>Tavish opened her mouth but no words came out. She felt like someone had just sucker-punched her in the stomach.</p>
<p>The woman’s lips tightened. “Did you think a man as handsome as he was would ever fall for <i>you</i>?” She held up the handkerchief Tavish had given her. “Who has embroidered initials on their handkerchiefs? Who even carries
            handkerchiefs anymore? You’re a plain, dumpy rich girl. That’s all he wanted. Your money.”
         </p>
<p>“He . . . he had money . . .”</p>
<p>“Is that what he told you? Ha! That ring on your finger is nothing more than cubic zirconia.”</p>
<p>Tavish’s head buzzed.</p>
<p>“Want more proof that he was engaged to me? That I knew him? Andrew loved IPA beer, wore his father’s dog tags, and hated
            his work. Shall I describe his—”
         </p>
<p>“No!”</p>
<p>“I knew about you, but you didn’t know about me. I told him he had to break up with you or I’d make sure this little bundle”—she
            rubbed her bulging stomach—“would be your next news flash. Andrew took the easy way out.” She turned to leave, then turned
            back. “None of this had to happen if you and all your money hadn’t come along. Now neither of us wins.” She pivoted and stalked
            away.
         </p>
<p>Tavish turned toward the open grave, staring at it blindly. She could barely breathe.</p>
<p>The funeral director coughed softly.</p>
<p>She blinked, dropped the rose’s broken stem, then crumpled the petals and let them drift through her fingers. Carefully she
            removed the engagement ring, lifted a corner of the fake grass, and shoved the ring into the dirt. She straightened, turned,
            and walked to her car, never looking back.
         </p>
</section>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Chapter_1.vue?ba9fa708